import React, { useRef, useState } from "react";
import styled from "styled-components";
import {
  FiAward,
  FiClock,
  FiCode,
  FiFolder,
  FiGithub,
  FiHome,
  FiMail,
  FiMenu,
  FiUser,
  FiX,
} from "react-icons/fi";

const links = [
  ["Home", "home", FiHome],
  ["About", "aboutme", FiUser],
  ["Tech-Stack", "techstack", FiCode],
  ["Projects", "projects", FiFolder],
  ["Timeline", "timeline", FiClock],
  ["Certificates", "certificates", FiAward],
];

const NavBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const menuButton = useRef(null);
  const closeMenu = () => setIsOpen(false);

  return (
    <Container
      onKeyDown={(event) => {
        if (event.key === "Escape" && isOpen) {
          closeMenu();
          menuButton.current?.focus();
        }
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) closeMenu();
      }}
    >
      <Brand href="#home" onClick={closeMenu}>
        Maximilian<span> Gaber</span>
        <span className="brand-dot">.</span>
      </Brand>
      <Nav
        id="primary-navigation"
        aria-label="Main navigation"
        $isOpen={isOpen}
      >
        {links.map(([label, id, Icon]) => (
          <a key={id} href={`#${id}`} onClick={closeMenu}>
            <Icon aria-hidden="true" />
            {label}
          </a>
        ))}
      </Nav>
      <Actions>
        <IconLink
          href="mailto:maxi.gaber@gmail.com"
          aria-label="Email Maximilian"
          title="Email Maximilian"
          onClick={closeMenu}
        >
          <FiMail aria-hidden="true" />
        </IconLink>
        <IconLink
          href="https://github.com/ukalto"
          aria-label="Maximilian on GitHub"
          title="GitHub"
          onClick={closeMenu}
        >
          <FiGithub aria-hidden="true" />
        </IconLink>
        <MenuButton
          ref={menuButton}
          type="button"
          aria-label={isOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={isOpen}
          aria-controls="primary-navigation"
          onClick={() => setIsOpen((open) => !open)}
        >
          {isOpen ? <FiX aria-hidden="true" /> : <FiMenu aria-hidden="true" />}
        </MenuButton>
      </Actions>
    </Container>
  );
};

export default NavBar;

const Container = styled.header`
  position: fixed;
  top: 1rem;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: calc(100% - 2rem);
  max-width: 1280px;
  margin-inline: auto;
  padding: 0.65rem 0.85rem 0.65rem 1.2rem;
  border: 1px solid rgba(43, 43, 53, 0.09);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  -webkit-backdrop-filter: blur(18px);
  backdrop-filter: blur(18px);
  box-shadow:
    0 8px 32px rgba(43, 43, 53, 0.08),
    0 2px 6px rgba(43, 43, 53, 0.03);

  a {
    color: var(---tertiary);
    text-decoration: none;
  }

  a:focus-visible,
  button:focus-visible {
    outline: 2px solid #00866a;
    outline-offset: 4px;
  }

  @media (min-width: 1200px) {
    padding-inline: 1.5rem 0.85rem;
    gap: 1.25rem;
  }

  @media (max-width: 839px) {
    width: calc(100% - 3rem);
  }

  @media (max-width: 420px) {
    width: calc(100% - 2rem);
    padding-inline: 0.75rem 0.375rem;
    gap: 0.375rem;
  }
`;

const Brand = styled.a`
  flex-shrink: 0;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: -0.04em;

  .brand-dot {
    color: var(---secondary);
  }

  @media (max-width: 420px) {
    flex-shrink: 1;
    min-width: 0;
    font-size: clamp(0.75rem, 3.5vw, 0.9rem);
  }
`;

const Nav = styled.nav`
  display: ${({ $isOpen }) => ($isOpen ? "flex" : "none")};
  position: absolute;
  top: calc(100% + 0.75rem);
  left: 0;
  right: 0;
  flex-direction: column;
  gap: 0.25rem;
  max-height: calc(100dvh - 7rem);
  overflow-y: auto;
  padding: 0.75rem;
  border: 1px solid rgba(43, 43, 53, 0.09);
  border-radius: 1.5rem;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 12px 32px rgba(43, 43, 53, 0.1);

  a {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    min-height: 44px;
    padding: 0.65rem 0.9rem;
    border-radius: 999px;
    font-size: 0.8rem;
    font-weight: 500;
    white-space: nowrap;
    transition:
      background-color 180ms ease,
      color 180ms ease;

    svg {
      width: 1rem;
      height: 1rem;
      flex-shrink: 0;
    }

    &:hover {
      background: rgba(1, 190, 150, 0.09);
      color: #007b61;
    }
  }

  @media (min-width: 1200px) {
    display: flex;
    position: static;
    flex-direction: row;
    max-height: none;
    overflow: visible;
    padding: 0;
    border: 0;
    background: transparent;
    box-shadow: none;

    a {
      padding-inline: 0.6rem;
    }
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.25rem;
`;

const IconLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(43, 43, 53, 0.04);
  font-size: 1.1rem;
  transition:
    background-color 180ms ease,
    color 180ms ease;

  &:hover {
    background: rgba(1, 190, 150, 0.14);
    color: #007b61;
  }
`;

const MenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(---tertiary);
  font-size: 1.25rem;
  cursor: pointer;

  &:hover {
    background: rgba(1, 190, 150, 0.09);
  }

  @media (min-width: 1200px) {
    display: none;
  }
`;
